import { ChessAppLayout } from "@/components/ChessAppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useSEO } from '@/hooks/useSEO';

const Terms = () => {
  // SEO Configuration
  useSEO({
    title: "Terms of Service - ChessHatch Legal Information",
    description: "Read ChessHatch Terms of Service including user responsibilities, fair play rules, account management, and legal guidelines for our chess learning platform.",
    keywords: "chess terms of service, chess platform rules, chess legal terms, user agreement, chess platform policies"
  });

  return (
    <ChessAppLayout>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-4">Terms of Service</h1>
          <p className="text-muted-foreground text-lg">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>1. Acceptance of Terms</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>
                By accessing and using ChessHatch, you accept and agree to be bound by the terms 
                and provision of this agreement. If you do not agree to abide by the above, 
                please do not use this service.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>2. Use License</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>
                Permission is granted to temporarily access ChessHatch for personal, 
                non-commercial transitory viewing only. This includes:
              </p>
              <ul className="list-disc ml-6 mt-2">
                <li>Playing chess games and variants</li>
                <li>Analyzing game positions</li>
                <li>Participating in community features</li>
                <li>Accessing educational content</li>
              </ul>
              <p className="mt-4">
                This license shall automatically terminate if you violate any of these 
                restrictions and may be terminated by us at any time.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>3. User Accounts</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>
                When you create an account with us, you must provide information that is 
                accurate, complete, and current at all times. You are responsible for 
                safeguarding the password and for any activities that occur under your account.
              </p>
              <p className="mt-4">
                You agree not to disclose your password to any third party and to take sole 
                responsibility for activities that occur under your account.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>4. Game Conduct</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>
                Users must maintain fair play and sportsmanlike conduct. The following 
                are strictly prohibited:
              </p>
              <ul className="list-disc ml-6 mt-2">
                <li>Using chess engines or computer assistance during games</li>
                <li>Creating multiple accounts to manipulate ratings</li>
                <li>Harassment or abusive behavior toward other players</li>
                <li>Intentionally disconnecting to avoid losses</li>
                <li>Sharing accounts or allowing others to play on your behalf</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>5. Community Guidelines</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>
                Our community features are designed to be welcoming and educational. 
                Users must:
              </p>
              <ul className="list-disc ml-6 mt-2">
                <li>Treat all members with respect and courtesy</li>
                <li>Keep discussions relevant to chess and the platform</li>
                <li>Avoid spam, promotional content, or off-topic material</li>
                <li>Report inappropriate behavior through proper channels</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>6. Privacy and Data</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>
                We collect and process personal data in accordance with our Privacy Policy. 
                By using ChessHatch, you consent to the collection and use of information 
                as outlined in our privacy practices.
              </p>
              <p className="mt-4">
                Game data, including moves and positions, may be stored for analysis, 
                improvement of services, and educational purposes.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>7. Intellectual Property</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>
                The service and its original content, features, and functionality are and 
                will remain the exclusive property of ChessHatch and its licensors. The 
                service is protected by copyright, trademark, and other laws.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>8. Termination</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>
                We may terminate or suspend your account and bar access to the service 
                immediately, without prior notice or liability, under our sole discretion, 
                for any reason whatsoever, including without limitation if you breach the 
                Terms of Service.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>9. Limitation of Liability</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>
                In no event shall ChessHatch, nor its directors, employees, partners, agents, 
                suppliers, or affiliates, be liable for any indirect, incidental, special, 
                consequential, or punitive damages, including without limitation, loss of 
                profits, data, use, goodwill, or other intangible losses.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>10. Changes to Terms</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>
                We reserve the right, at our sole discretion, to modify or replace these 
                Terms at any time. If a revision is material, we will provide at least 
                30 days notice prior to any new terms taking effect.
              </p>
            </CardContent>
          </Card>

          <Separator className="my-8" />

          <Card className="bg-muted/30">
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
              <CardDescription>
                Questions about our Terms of Service? Get in touch.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                If you have any questions about these Terms of Service, please contact us 
                through our community channels or support system.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </ChessAppLayout>
  );
};

export default Terms;